<script setup lang="ts">
import type { SurpriseBag } from '@pkg/types/surpriseBag';
import { THEME } from '@pkg/config/misc/theme';
import Section from '@pkg/modular-blocks/blocks/outer/Section/Section.vue';
import BrandProfileCitiesList from './components/BrandProfileCitiesList.vue';
import BrandProfileHero from './components/BrandProfileHero.vue';
import BrandProfileSurpriseBags from './components/BrandProfileSurpriseBags.vue';
import {
  brandProfileCityUrl,
  brandProfilePageUrl,
  parseBrandProfileRoute,
  resolvePartnerSlugSegment,
} from './utils/brandProfileUrls';
import type { BrandProfileSanityDoc, BrandProfileMarketingDoc } from '~/app/types/brandProfiles';

const props = defineProps<{
  content?: {
    _type: string;
    title?: string;
    seoSocial?: unknown;
    brandProfileSanity?: BrandProfileSanityDoc | null;
    brandProfileMarketing?: BrandProfileMarketingDoc | null;
    cities?: string[];
    brandProfileSurpriseBagItems?: SurpriseBag[];
  } | null;
}>();

const router = useRouter();
const route = useRoute();
const { locale } = useI18n();

const brandName = computed(() =>
  props.content?.brandProfileSanity?.name?.trim() ||
  props.content?.title?.trim() ||
  'Partner',
);

/** SEO-optimised H1. Falls back to the canonical brand name when no Page Title is set. */
const pageH1 = computed(() =>
  props.content?.brandProfileSanity?.pageTitle?.trim() || brandName.value,
);

const chainId = computed(() => props.content?.brandProfileSanity?.chainId?.trim() || '');
const cities = computed<string[]>(() => props.content?.cities ?? []);

const partnerSlugSegment = computed(() =>
  resolvePartnerSlugSegment({
    profileSlug: props.content?.brandProfileSanity?.profileSlug,
    path: route.path,
    brandNameFallback: brandName.value,
  }),
);

const pageUrl = computed(() => brandProfilePageUrl(locale.value, partnerSlugSegment.value));
const showCitiesList = ref(true);
const selectedCity = ref('');

const pageTitle = computed(() =>
  showCitiesList.value
    ? pageH1.value
    : `${brandName.value} - ${selectedCity.value}`,
);

function cityPagePath(city: string): string {
  return brandProfileCityUrl(locale.value, city, partnerSlugSegment.value);
}

function resolveSelectedCityFromUrl(): void {
  const parsed = parseBrandProfileRoute(route.path);
  if (parsed.kind !== 'city' || !parsed.citySlugLower) { return; }

  showCitiesList.value = false;

  const rawCn = route.query.cn;
  const queryCityName = Array.isArray(rawCn) ? rawCn[0] : rawCn;
  if (queryCityName) {
    selectedCity.value = queryCityName;
    return;
  }

  const match = cities.value.find(c => c.trim().toLowerCase() === parsed.citySlugLower);
  if (match) {
    selectedCity.value = match;
  }
}

resolveSelectedCityFromUrl();

watch(cities, (loaded) => {
  if (!showCitiesList.value && !selectedCity.value && loaded.length > 0) {
    resolveSelectedCityFromUrl();
  }
});

function handleCityClick(city: string): void {
  selectedCity.value = city;
  showCitiesList.value = false;
  window.scrollTo({ top: 0, behavior: 'smooth' });
  router.push({ path: cityPagePath(city), query: { cn: city } });
}

function handleShowCitiesList(): void {
  showCitiesList.value = true;
  selectedCity.value = '';
  router.push(pageUrl.value);
}
</script>

<template>
  <Section :theme="THEME.APP" variant="noImage" loading="lazy">
    <article class="s-brand__wrapper" :data-theme="THEME.APP">
      <BrandProfileHero
        :content="content"
        :page-title="pageTitle"
        :selected-city="selectedCity"
        :show-cities-list="showCitiesList"
        @change-to-list="handleShowCitiesList"
      />

      <BrandProfileCitiesList
        v-if="showCitiesList"
        :brand-name="brandName"
        :cities="cities"
        :cities-list-title="content?.brandProfileSanity?.brandProfileDefaultTexts?.citiesListTitle"
        :no-cities-available-text="content?.brandProfileSanity?.brandProfileDefaultTexts?.noCitiesAvailableText"
        @city-click="handleCityClick"
      />

      <BrandProfileSurpriseBags
        v-else
        :city="selectedCity"
        :chain-id="chainId"
        :brand-name="brandName"
        :surprise-bags-list-title="content?.brandProfileSanity?.brandProfileDefaultTexts?.surpriseBagsListTitle"
        :empty-state-text="content?.brandProfileSanity?.brandProfileDefaultTexts?.emptyStateText"
      />
    </article>
  </Section>
</template>

<style scoped>
@import '~/assets/css/mixins/_mixins.index.pcss';

.s-brand__wrapper {
  background-color: white;
  @mixin body-large;
  color: var(--theme-text-color-app);
  width: 100%;
  padding: theme('spacing.20');
  text-align: left;
  overflow-x: hidden;

  @screen tablet-wide {
    padding: theme('spacing.20') theme('spacing.96');
  }
  @screen desk {
    padding: theme('spacing.40') theme('spacing.96');
  }
  @screen big-desk {
    padding: theme('spacing.20') theme('spacing.96');
  }
}
</style>
